// src/pages/calendar/bookings/utils/emailUtils.js
import { axiosInstance } from "../../../../utils/axios";

/**
 * Send an email based on template
 * @param {Object} booking - The booking object
 * @param {Object} eventType - The event type object
 * @param {string} template - Email template type (cancellation, reschedule, requestReschedule, etc.)
 * @param {Object} additionalData - Additional data for email templates
 * @returns {Promise} - Resolves when email is sent
 */
export const sendEmail = async (
  booking,
  eventType,
  template,
  additionalData = {}
) => {
  try {
    // Common data for all email templates
    const commonData = {
      userName: booking.name,
      eventTitle: eventType.title,
      eventDescription: eventType.description,
      bookingDate: booking.date,
      bookingTime: `${booking.timeStart} - ${booking.timeEnd}`,
      timezone: booking.timezone || "UTC",
    };

    // Merge with additional data
    const mergedData = { ...commonData, ...additionalData };

    // For cancellation template, add rebookLink if not provided
    if (
      (template === "cancellation" || template === "bookingRejection") &&
      !additionalData.rebookLink &&
      !mergedData.bookingPreviewUrl
    ) {
      mergedData.rebookLink = `${window.location.origin}/preview/preview-event-type?eventTypeId=${eventType.id}`;
    }

    // For requestReschedule, add managementToken and rebookLink
    if (template === "requestReschedule") {
      if (booking.managementToken) {
        mergedData.managementToken = booking.managementToken;
      }
      if (!mergedData.rebookLink && mergedData.bookingPreviewUrl) {
        mergedData.rebookLink = mergedData.bookingPreviewUrl;
      }
    }

    // Send email request to API
    const response = await axiosInstance.post("/api/send-email", {
      to: booking.email,
      subject: getEmailSubject(template, eventType.title),
      template: mapTemplateToApiTemplate(template),
      data: mergedData,
    });

    // Check for success directly from the axios response
    if (response.data && response.data.success === true) {
      console.log(`${template} email sent successfully`);
      return true;
    } else {
      console.warn("Email API returned non-success response:", response.data);
      // Don't throw an error here, just log and continue
      return true; // Still return true since API may have worked
    }
  } catch (error) {
    console.error(`Error sending ${template} email:`, error);
    // Just log the error but don't throw it - this prevents the process
    // from failing even if the email sending fails
    return true; // Return true to allow the process to continue
  }
};

/**
 * Map internal template name to API template name
 * @param {string} internalTemplateName - Internal template name
 * @returns {string} - API template name
 */
function mapTemplateToApiTemplate(internalTemplateName) {
  // Map our internal template names to API template names
  const templateMap = {
    cancellation: "cancellation",
    reschedule: "reschedule",
    requestReschedule: "requestReschedule",
    bookingConfirmation: "bookingConfirmation",
    bookingRejection: "bookingRejection",
    newBooking: "newBooking",
  };

  return templateMap[internalTemplateName] || internalTemplateName;
}

/**
 * Get email subject based on template and event title
 * @param {string} template - Email template
 * @param {string} eventTitle - Event title
 * @returns {string} - Email subject
 */
function getEmailSubject(template, eventTitle) {
  switch (template) {
    case "cancellation":
      return `Booking Canceled: ${eventTitle}`;
    case "reschedule":
      return `Booking Rescheduled: ${eventTitle}`;
    case "requestReschedule":
      return `Action Required: Please Reschedule Your Booking for ${eventTitle}`;
    case "bookingConfirmation":
      return `Booking Confirmed: ${eventTitle}`;
    case "bookingRejection":
      return `Booking Declined: ${eventTitle}`;
    case "newBooking":
      return `New Booking: ${eventTitle}`;
    default:
      return `Booking Update: ${eventTitle}`;
  }
}

/**
 * Send a test email to verify the email configuration
 * @param {string} email - Recipient email address
 * @returns {Promise} - Resolves when email is sent
 */
export const sendTestEmail = async (email) => {
  try {
    const response = await axiosInstance.post("/api/send-email", {
      to: email,
      template: "test",
      data: {
        userName: "Test User",
        eventTitle: "Test Event",
        bookingDate: "2025-01-01",
        bookingTime: "10:00 AM - 11:00 AM",
        timezone: "UTC",
      },
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to send test email");
    }

    return true;
  } catch (error) {
    console.error("Error sending test email:", error);
    throw new Error("Failed to send test email");
  }
};

/**
 * Send notification email to the event owner about a new booking
 * @param {Object} booking - The booking object
 * @param {Object} eventType - The event type object
 * @param {string} ownerEmail - Event owner's email
 * @returns {Promise} - Resolves when email is sent
 */
export const sendBookingNotificationToOwner = async (
  booking,
  eventType,
  ownerEmail
) => {
  try {
    const response = await axiosInstance.post("/api/send-email", {
      to: ownerEmail,
      template: "newBooking",
      data: {
        eventTitle: eventType.title,
        bookingDate: booking.date,
        bookingTime: `${booking.timeStart} - ${booking.timeEnd}`,
        timezone: booking.timezone || "UTC",
        bookerName: booking.name,
        bookerEmail: booking.email,
        notes: booking.notes || "No notes provided",
      },
    });

    if (!response.data?.success) {
      throw new Error(
        response.data?.message || "Failed to send owner notification"
      );
    }

    return true;
  } catch (error) {
    console.error("Error sending owner notification:", error);
    throw new Error("Failed to send owner notification");
  }
};
